import { defineStore } from 'pinia'
import moment from 'moment'
import { computed, Ref, ref, UnwrapRef } from 'vue'
import currencyJs from 'currency.js'
import { PaymentRequestForm, PaymentRequestStatus } from '@/components/PaymentRequest/types'
import { RateType } from '@/enums'
import {
  MarketEnum,
  PaymentRequestTypeEnum,
  TimesheetTypeEnum,
} from '@/components/PaymentRequest/enums'
import { useTimesheetStore } from '@/components/PaymentRequest/store/useTimesheetStore'
import { useMilestoneStore } from '@/components/PaymentRequest/store/useMilestoneStore'
import { $t } from '@/composables/translation'

export type PaymentRequestPeriod = {
  isoWeek: number | null
  startDate: string
  endDate: string
  isBilled?: boolean
}

export type ContractForPeriod = {
  rate: number | null
  rateType: string | null
  currency: string | null
}

export const usePaymentRequestStore = defineStore('payment-request', () => {
  const timesheetStore = useTimesheetStore()
  const milestoneStore = useMilestoneStore()

  const paymentRequestId = ref(0)
  const isEditable = ref(true)
  const hasPayroll = ref(false)
  const isFetchingData = ref(false)
  const isSubmitting = ref(false)
  const hireGlobalId = ref('')
  const hireLocalId: Ref<UnwrapRef<string | number>> = ref('')
  const workerId = ref('')
  const enableWorksomeIntelligence = ref(false)
  const requiresStartAndEndTimesOnTimesheets = ref(false)
  const enableAiTimesheetParsing = computed(
    () => enableWorksomeIntelligence.value && !requiresStartAndEndTimesOnTimesheets.value,
  )

  // @ts-ignore
  const { thousandsSeparator, decimalSeparator } = window.currentLocale
  const currencyConfig = { pattern: `#`, separator: thousandsSeparator, decimal: decimalSeparator }

  const paymentRequestFormAction: Ref<'/bills' | '/bills/draft'> = ref('/bills')

  // Externally provided data (via props for now)
  const paymentRequestRate = ref({
    rate: 0,
    rateType: RateType.DAILY,
    rateTypeName: '',
    rateTypeNameSlash: '',
  })
  const hireRate = ref({
    rate: 0,
    rateType: RateType.DAILY,
    rateTypeName: '',
    rateTypeNameSlash: '',
  })
  const hasFreelancerVat = ref(false)
  const market: Ref<MarketEnum | null> = ref(null)
  const currency = ref('')
  const vatRate = ref(0)
  const vatTerminology = ref('VAT')
  const billId = ref(0)
  const jobId = ref(0)
  const threadId = ref(0)
  const worksomeFee = ref(0)
  const companyName = ref('')
  const companyId = ref('')
  // On a supplier-mirror hire this also carries the client company, so the
  // worker is offered the client's payment-request custom fields.
  const customFieldProviderAccountIds = ref<string[]>([])
  const jobName = ref('')
  const hireStartDate: Ref<string | null> = ref(null)
  const hireEndDate: Ref<string | null> = ref(null)
  // Dates on which the hire's rate changed or will change, fetched from the
  // hire so we can warn when the chosen billing period straddles one.
  const rateChangeDates = ref<string[]>([])
  // When the hire requires payment request periods, the worker picks one of
  // these periods instead of entering free dates, except for days before the
  // date the rule starts.
  const requiresPaymentRequestPeriods = ref(false)
  const paymentRequestPeriods = ref<PaymentRequestPeriod[]>([])
  const paymentRequestPeriodsStartDate = ref<string | null>(null)
  const isLoadingPaymentRequestPeriods = ref(false)
  const isLoadingRateForPeriod = ref(false)
  // A saved bill's own period, which may still be saved if it already spans
  // a change (mirrors the backend's Hire::rateChangeOverlap exemption).
  const savedBillPeriod = ref<{
    dateStart: string | null
    dateEnd: string | null
    billsTime?: boolean
  } | null>(null)
  // Form state
  const paymentRequestStatus: Ref<PaymentRequestStatus> = ref(null)
  const isDraft = computed(() => paymentRequestStatus.value === 'draft')
  const paymentRequestType = ref(PaymentRequestTypeEnum.TIME_X_RATE)

  const getFreshPaymentRequestForm = (): PaymentRequestForm =>
    ({
      dateStart: undefined,
      dateEnd: moment().format('YYYY-MM-DD'),
      billableTime: 0,
      billableTotal: currencyJs(0, currencyConfig).format(),
      timesheetId: 0,
      comments: '',
      timesheetConfirmed: false,
      milestoneId: null,
    }) as PaymentRequestForm

  const paymentRequestForm: Ref<PaymentRequestForm> = ref(getFreshPaymentRequestForm())

  const rateChangeBoundaryDate = computed(() => {
    const start = paymentRequestForm.value.dateStart
    if (!start) return null
    if (
      savedBillPeriod.value?.dateStart === start &&
      savedBillPeriod.value?.dateEnd === (paymentRequestForm.value.dateEnd || null)
    ) {
      return null
    }
    // No end date means a single-day request on the start date (mirrors the
    // backend's Hire::billingPeriodCrossesRateChange semantics).
    const end = paymentRequestForm.value.dateEnd || start
    return rateChangeDates.value.find((date) => date > start && date <= end) ?? null
  })

  // With overlapping payment requests prevented, a period already billed by time is refused for
  // another request that bills time on an hourly, daily or weekly rate. A fixed amount or expenses
  // may still cover a billed period.
  const chargesTimeOnTimeRate = computed(
    () =>
      paymentRequestType.value === PaymentRequestTypeEnum.TIME_X_RATE &&
      [RateType.HOURLY, RateType.DAILY, RateType.WEEKLY].includes(paymentRequestRate.value.rateType),
  )

  const isSavedPeriod = (period: PaymentRequestPeriod) =>
    period.startDate === savedBillPeriod.value?.dateStart &&
    period.endDate === savedBillPeriod.value?.dateEnd

  // The overlap check leaves out the request itself, so a submitted request billing time keeps its
  // own period.
  const keepsOwnBilledPeriod = (period: PaymentRequestPeriod) =>
    !isDraft.value && !!savedBillPeriod.value?.billsTime && isSavedPeriod(period)

  // Billed periods are left out, except the request's own: a submitted request keeps it, and a
  // draft shows the period it was saved with so the worker sees it is billed and picks another.
  const selectablePaymentRequestPeriods = computed(() =>
    chargesTimeOnTimeRate.value
      ? paymentRequestPeriods.value.filter(
          (period) =>
            !period.isBilled || keepsOwnBilledPeriod(period) || (isDraft.value && isSavedPeriod(period)),
        )
      : paymentRequestPeriods.value,
  )

  const pickedPaymentRequestPeriodIsBilled = computed(() => {
    const { dateStart, dateEnd } = paymentRequestForm.value
    const picked = paymentRequestPeriods.value.find(
      (period) => period.startDate === dateStart && period.endDate === dateEnd,
    )

    return (
      chargesTimeOnTimeRate.value && !!picked?.isBilled && !keepsOwnBilledPeriod(picked)
    )
  })

  // Nothing can be picked when every period so far is billed or none has started. A later start
  // date still offers the days before it, and a submitted request its own saved period.
  const hasNoPaymentRequestPeriodToBill = computed(
    () =>
      requiresPaymentRequestPeriods.value &&
      paymentRequestType.value !== PaymentRequestTypeEnum.MILESTONE &&
      !isLoadingPaymentRequestPeriods.value &&
      !paymentRequestPeriodsStartDate.value &&
      selectablePaymentRequestPeriods.value.length === 0 &&
      (isDraft.value || !savedBillPeriod.value?.dateStart || !savedBillPeriod.value.dateEnd),
  )

  // Actions
  const updatePaymentRequestType = (newPaymentRequestType: PaymentRequestTypeEnum) => {
    paymentRequestType.value = newPaymentRequestType
    if (newPaymentRequestType === PaymentRequestTypeEnum.MILESTONE) {
      paymentRequestForm.value.billableTime = 0
      paymentRequestForm.value.billableTotal = currencyJs(0, currencyConfig).format()
    }
  }

  const clearPaymentRequest = () => {
    paymentRequestForm.value = getFreshPaymentRequestForm()
    hireStartDate.value = null
    hireEndDate.value = null
    savedBillPeriod.value = null
  }

  const hydratePaymentRequestStoreFromHire = (hire) => {
    hasPayroll.value = hire.hasPayroll
    hasFreelancerVat.value = hire.hasFreelancerVat
    market.value = hire.market
    currency.value = hire.currency
    worksomeFee.value = hire.freelancerFee
    threadId.value = hire.threadId
    jobId.value = hire.yjob.localId
    jobName.value = hire.yjob.name
    companyName.value = hire.company.name
    companyId.value = hire.company.globalId
    customFieldProviderAccountIds.value = (hire.customFieldProviderCompanies ?? []).map(
      (company) => company.globalId,
    )
    hireStartDate.value = hire.dateStart ?? null
    hireEndDate.value = hire.dateEnd ?? null
    hireRate.value = {
      rate: hire.rate,
      rateType: hire.ratetype,
      rateTypeName: hire.ratetypeName,
      rateTypeNameSlash: hire.ratetypeNameSlash,
    }
    paymentRequestRate.value = {
      rate: hire.rate,
      rateType: hire.ratetype,
      rateTypeName: hire.ratetypeName,
      rateTypeNameSlash: hire.ratetypeNameSlash,
    }

    milestoneStore.milestones = hire.milestones.data

    updatePaymentRequestType(
      hire.ratetype === RateType.FIXED
        ? hire.milestones.data.length > 0
          ? PaymentRequestTypeEnum.MILESTONE
          : PaymentRequestTypeEnum.SPECIFY_AMOUNT
        : PaymentRequestTypeEnum.TIME_X_RATE,
    )

    applyHireEndDateDefault()
  }

  const applyHireEndDateDefault = () => {
    if (hireEndDate.value) {
      const hireEnd = moment(hireEndDate.value, 'YYYY-MM-DD')
      if (hireEnd.isBefore(moment(), 'day')) {
        paymentRequestForm.value.dateEnd = hireEnd.format('YYYY-MM-DD')
      }
    }
  }

  // The rate of the contract in effect for the picked period. A specified
  // amount or milestone keeps its own rate; only the reference rate follows.
  const applyRateForPeriod = (contract: ContractForPeriod | null | undefined) => {
    if (!contract) return
    const { rate } = contract
    if (rate === null || rate === undefined) return

    const rateType = (contract.rateType?.toLowerCase() ?? '') as RateType
    const rateFor = () => ({
      rate,
      rateType,
      rateTypeName: rateType ? $t(`rate.${rateType}`) : '',
      rateTypeNameSlash: rateType ? $t(`rate.${rateType}_slash`) : '',
    })

    hireRate.value = rateFor()

    if (paymentRequestType.value === PaymentRequestTypeEnum.TIME_X_RATE) {
      paymentRequestRate.value = rateFor()
    }

    if (contract.currency) {
      currency.value = contract.currency
    }
  }

  const hydratePaymentRequestStoreFromBill = (bill) => {
    const timesheetTypeOptionMatchingBill = bill.worksomeTimesheet
      ? timesheetStore.timesheetTypeOptions.find(
          (option) => option.value === TimesheetTypeEnum.TIMESHEET,
        )
      : timesheetStore.timesheetTypeOptions.find(
          (option) => option.value === (bill.timeSheetFormat || TimesheetTypeEnum.NONE),
        )
    if (timesheetTypeOptionMatchingBill) {
      timesheetStore.timesheetType = timesheetTypeOptionMatchingBill
    }
    if (bill.localId) {
      paymentRequestId.value = bill.localId
    }
    if (bill.isDraft) {
      paymentRequestStatus.value = 'draft'
    }
    if (bill.isIssued) {
      paymentRequestStatus.value = 'issued'
    }
    if (bill.isApproved) {
      paymentRequestStatus.value = 'approved'
    }
    isEditable.value = bill.canBeEdited
    if (bill.currency) {
      currency.value = bill.currency
    }

    if (bill.milestones.data.length > 0) {
      milestoneStore.selectMilestone(bill.milestones.data[0].id)
      paymentRequestForm.value.milestoneId = bill.milestones.data[0].id
    }

    paymentRequestForm.value.comments = bill.message
    switch (bill.rateType) {
      case RateType.FIXED:
        if (paymentRequestType.value === PaymentRequestTypeEnum.MILESTONE) {
          break
        }

        updatePaymentRequestType(PaymentRequestTypeEnum.SPECIFY_AMOUNT)
        // since the fixed amount is stored in the rate field, we need to
        // set the billableTotal from that
        if (bill.rate > 0) {
          paymentRequestForm.value.billableTotal = bill.rate.toFixed(2)
        }
        break
      case RateType.MONTHLY:
        updatePaymentRequestType(PaymentRequestTypeEnum.SPECIFY_AMOUNT)
        // since the fixed amount is stored in the rate field, we need to
        // set the billableTotal from that
        if (bill.rate > 0) {
          paymentRequestForm.value.billableTotal = bill.rate.toFixed(2)
        }
        break
      case RateType.HOURLY:
        updatePaymentRequestType(PaymentRequestTypeEnum.TIME_X_RATE)
        paymentRequestForm.value.billableTime = bill.hours
        break
      case RateType.DAILY:
        updatePaymentRequestType(PaymentRequestTypeEnum.TIME_X_RATE)
        paymentRequestForm.value.billableTime = bill.days
        break
      case RateType.WEEKLY:
        updatePaymentRequestType(PaymentRequestTypeEnum.TIME_X_RATE)
        paymentRequestForm.value.billableTime = bill.weeks
        break
    }
    // @todo should put "Support old bills which were allowed a different rate and/or rate type than" from PagePaymentRequest section here, but relies on hire
    if (bill.dateStart) {
      paymentRequestForm.value.dateStart = moment(bill.dateStart, 'YYYY-MM-DD').format('YYYY-MM-DD')
    }
    if (bill.dateEnd) {
      paymentRequestForm.value.dateEnd = moment(bill.dateEnd, 'YYYY-MM-DD').format('YYYY-MM-DD')
    }
    // The bill's own dates, not the form's, which may carry the hire's end date
    savedBillPeriod.value = {
      dateStart: bill.dateStart ? moment(bill.dateStart, 'YYYY-MM-DD').format('YYYY-MM-DD') : null,
      dateEnd: bill.dateEnd ? moment(bill.dateEnd, 'YYYY-MM-DD').format('YYYY-MM-DD') : null,
      billsTime: bill.hours > 0 || bill.days > 0 || bill.weeks > 0,
    }
  }

  const enableWorksomeTimesheetLinesFeature = ref(false)
  const eligibleForWorksomeTimesheet = computed(() =>
    [RateType.HOURLY, RateType.DAILY].includes(paymentRequestRate.value.rateType),
  )

  return {
    isDraft,
    isFetchingData,
    isSubmitting,
    currency,
    paymentRequestStatus,
    paymentRequestFormAction,
    currencyConfig,
    paymentRequestForm,
    paymentRequestRate,
    hireRate,
    paymentRequestType,
    vatRate,
    vatTerminology,
    jobId,
    worksomeFee,
    threadId,
    billId,
    companyId,
    customFieldProviderAccountIds,
    companyName,
    jobName,
    market,
    hasFreelancerVat,
    hasPayroll,
    isEditable,
    hireGlobalId,
    hireLocalId,
    hireStartDate,
    hireEndDate,
    rateChangeDates,
    requiresPaymentRequestPeriods,
    paymentRequestPeriods,
    paymentRequestPeriodsStartDate,
    isLoadingPaymentRequestPeriods,
    selectablePaymentRequestPeriods,
    hasNoPaymentRequestPeriodToBill,
    pickedPaymentRequestPeriodIsBilled,
    rateChangeBoundaryDate,
    isLoadingRateForPeriod,
    savedBillPeriod,
    workerId,
    paymentRequestId,
    enableWorksomeTimesheetLinesFeature,
    enableWorksomeIntelligence,
    enableAiTimesheetParsing,
    requiresStartAndEndTimesOnTimesheets,
    eligibleForWorksomeTimesheet,
    updatePaymentRequestType,
    clearPaymentRequest,
    hydratePaymentRequestStoreFromHire,
    hydratePaymentRequestStoreFromBill,
    applyHireEndDateDefault,
    applyRateForPeriod,
  }
})
